export type Bonus = {
  id: string
  title: string
  tagline: string
  intro: string[]
  listLabel: string
  bullets: string[]
  flow?: { label?: string; steps: string[] }
  extraList?: { label: string; items: string[] }
  closing?: string
}

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: 'Barre Instructor Cueing Playbook',
    tagline: 'Know What to Say — Not Just What to Demonstrate',
    intro: [
      'A good Barre instructor does more than show movements.',
      'The right cue can completely change how a student understands a position.',
    ],
    listLabel: 'This guide gives you ready-to-use coaching language for:',
    bullets: [
      'posture',
      'alignment',
      'pliés',
      'relevés',
      'glutes',
      'thighs',
      'calves',
      'balance',
      'core',
      'upper body',
      'pulses',
      'holds',
      'transitions',
      'equipment work',
    ],
    extraList: {
      label: 'Includes:',
      items: [
        'Setup Cues',
        'Action Cues',
        'Alignment Cues',
        'Correction Cues',
        'Modification Cues',
        'Level-Up Cues',
      ],
    },
    closing: 'Clearer cues. Faster corrections. More confident teaching.',
  },
  {
    id: '02',
    title: 'Barre Modifications & Progressions Handbook',
    tagline: 'One Class. Different Students. One Clear System.',
    intro: [
      'Not every student needs the exact same version of every exercise.',
      'This guide helps you adapt Barre movements without creating three completely different classes.',
    ],
    listLabel: 'Adjust:',
    bullets: [
      'range',
      'support',
      'duration',
      'tempo',
      'repetitions',
      'resistance',
      'balance demand',
      'equipment',
      'hold time',
      'pulse volume',
    ],
    flow: {
      label: 'For major movement families, get:',
      steps: ['Easier', 'Standard', 'Level up'],
    },
    extraList: {
      label: 'Use it for:',
      items: [
        'beginners',
        'mixed-level groups',
        'developing students',
        'more experienced students',
      ],
    },
    closing:
      'Modify faster. Progress intelligently. Keep the whole class moving.',
  },
  {
    id: '03',
    title: 'Barre Class Planning & Sequencing System',
    tagline: 'Build Better Classes Without Starting From Zero',
    intro: [
      'Even with a large class library, understanding how to structure Barre programming gives you even more flexibility.',
    ],
    listLabel: 'This guide gives you repeatable formulas for:',
    bullets: [
      '30-minute classes',
      '45-minute classes',
      '50-minute classes',
      '60-minute classes',
      'beginner Barre',
      'lower-body Barre',
      'glute-focused Barre',
      'full-body Barre',
      'mini-band Barre',
      'ball Barre',
      'slow-control Barre',
      'balance Barre',
    ],
    flow: {
      label: 'Learn how to organize:',
      steps: [
        'Warm-up',
        'Thighs',
        'Glutes',
        'Core',
        'Upper body',
        'Balance',
        'Mat work',
        'Finisher',
        'Release',
      ],
    },
    closing:
      'Plan faster. Sequence better. Stop rebuilding everything every week.',
  },
  {
    id: '04',
    title: 'Barre Alignment & Common Mistakes Handbook',
    tagline: 'See the Mistake. Correct It Faster.',
    intro: [
      'Good Barre instruction depends on being able to recognize when alignment starts to break down.',
    ],
    listLabel:
      'This guide helps you identify and correct common issues involving:',
    bullets: [
      'knee tracking',
      'foot positioning',
      'turnout',
      'pelvis position',
      'hip rotation',
      'rib position',
      'supporting leg',
      'excessive range',
      'Barre dependence',
      'lower-back compensation',
      'shoulder tension',
      'ankle alignment',
    ],
    flow: {
      label: 'Use the system:',
      steps: [
        'Movement',
        'Common error',
        'Quick correction',
        'Verbal cue',
        'Modification',
      ],
    },
    closing:
      'Less guessing. Clearer corrections. More consistent technique.',
  },
  {
    id: '05',
    title: '50 Barre Class Themes & Focus Ideas',
    tagline: 'Keep Your Class Schedule Fresh for Months',
    intro: [
      'Running out of ideas often leads instructors back to the same familiar class structure.',
      'This guide gives you 50 different Barre class concepts you can rotate throughout your schedule.',
    ],
    listLabel: 'Examples include:',
    bullets: [
      'Glute Burn Barre',
      'Thigh Endurance',
      'Inner-Thigh Focus',
      'Outer-Thigh Sculpt',
      'Balance Barre',
      'Slow Control',
      'Pulse & Hold',
      'Mini-Band Barre',
      'Ball Barre',
      'No-Equipment Barre',
      'Full-Body Barre',
      'Core & Stability',
      'Beginner Foundations',
      'Standing Barre',
      'Barre + Mat',
      'Single-Leg Barre',
      'Thighs + Calves',
    ],
    extraList: {
      label: 'Each theme includes:',
      items: [
        'main objective',
        'muscle focus',
        'level',
        'intensity',
        'recommended equipment',
        'suggested class structure',
      ],
    },
    closing: 'More variety. Less planning. Fewer repeated classes.',
  },
]
